import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { SITE, whatsappWithText } from "@/lib/site";

const NAV = [
  { to: "/services", label: "Services" },
  { to: "/universities", label: "Universities" },
  { to: "/success-stories", label: "Stories" },
  { to: "/about", label: "About" },
  { to: "/pricing", label: "Pricing" },
  { to: "/guarantee", label: "Guarantee" },
  { to: "/contact", label: "Contact" },
];

const linkCls = ({ isActive }) =>
  `text-sm font-medium transition-colors duration-200 ${
    isActive ? "text-[#1E2A78]" : "text-slate-500 hover:text-[#1E2A78]"
  }`;

export const Header = () => {
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="flex items-baseline gap-2" onClick={() => setOpen(false)}>
          <span className="font-display font-extrabold text-lg tracking-tight text-[#1E2A78] leading-none">
            MBA Project<span className="text-[#2563EB]"> &amp;</span> Thesis
          </span>
          <span className="hidden sm:inline text-[10px] uppercase tracking-[0.2em] text-slate-400 font-medium">
            Writing Help
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} className={linkCls} data-testid={`nav-link-${item.label.toLowerCase()}`}>
              {item.label}
            </NavLink>
          ))}
          <a
            href={whatsappWithText("Hi, I'd like a free quote.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-quote-button"
            className="rounded-full bg-gradient-to-r from-[#2563EB] to-[#4F46E5] text-white text-sm font-semibold px-5 py-2.5 shadow-md shadow-blue-500/25 transition-[opacity,transform] duration-200 hover:opacity-90 hover:-translate-y-0.5"
          >
            Get a Free Quote
          </a>
        </nav>

        <button
          className="lg:hidden p-2 text-[#1E2A78]"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" strokeWidth={1.5} /> : <Menu className="h-6 w-6" strokeWidth={1.5} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white border-b border-slate-200"
            data-testid="mobile-nav"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {[{ to: "/", label: "Home" }, ...NAV].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className="py-3 text-base font-medium text-slate-600 border-b border-slate-100"
                >
                  {item.label}
                </Link>
              ))}
              <a
                href={SITE.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 mb-2 rounded-full bg-gradient-to-r from-[#2563EB] to-[#4F46E5] text-white text-center text-sm font-semibold px-5 py-3"
              >
                Get a Free Quote
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
